<script lang="ts">
    import type {CssSize} from 'shared/ui-widgets/types';
    import type {TabGroup, TabId} from './types';
    import {fly} from 'svelte/transition';
    import {createEventDispatcher, onDestroy, onMount} from 'svelte';
    import {createTabPagesMenuContext} from './menu/tab-pages-menu.context';
    import {getInjector} from 'src/core';
    import {cleanup, exists} from 'shared/utils/custom-utils';
    import TabPagesMenu from './menu/TabPagesMenu.svelte';
    import {TabPagesUrlManagementService} from 'src/features/erp/components';

    export let pageClass: string;
    export let parentIdentifier: string;
    export let gap: CssSize = '30px';
    export let withoutUrlManagement = false;
    export let tabToSelect: TabId | null = null;
    export let tabGroups: TabGroup[] = [];

    const {selectTab, selectedTab$} = createTabPagesMenuContext(`${pageClass}-${parentIdentifier}`, tabGroups);
    const service = getInjector().getByClass(TabPagesUrlManagementService);
    const dispatch = createEventDispatcher<{ 'tab-changed': TabId }>();
    let urlStateInitialized = false;
    const urlStateSubscription = service.getUrlState$().subscribe((urlState) => {
        if (!urlStateInitialized) return;
        if (exists(urlState.activeTab) && isValidTabId(urlState.activeTab)) {
            selectTab(urlState.activeTab);
        }
    });

    onMount(async () => {
        if (withoutUrlManagement) {
            return;
        }

        const currentUrlState = await service.getCurrentUrlState();

        if (exists(currentUrlState) && exists(currentUrlState.activeTab) && isValidTabId(currentUrlState.activeTab)) {
            selectTab(currentUrlState.activeTab);
        }

        urlStateInitialized = true;
    });

    onDestroy(() => {
        cleanup(urlStateSubscription);
    });

    function isValidTabId(tabId: TabId) {
        const flattedTabs = tabGroups.flatMap((group) => group.subtabs ?? [group]);
        return flattedTabs.some((tab) => tab.id === tabId);
    }

    $: if (urlStateInitialized) service.setActiveTab($selectedTab$.id);
    $: if (exists(tabToSelect) && isValidTabId(tabToSelect)) selectTab(tabToSelect);
    $: dispatch('tab-changed', $selectedTab$.id);
</script>

{#if (!withoutUrlManagement && urlStateInitialized) || withoutUrlManagement}
    <div class="erp-tabbed-subpages-container {pageClass}">
        <div class="tab-menu-container">
            <TabPagesMenu/>

            {#if exists($$slots['additional-content'])}
                <slot name="additional-content" activeTab={$selectedTab$.id}/>
            {/if}
        </div>

        <div class="subpage-container-wrapper"
             class:with-padding={$selectedTab$.render.padded}
             aria-live="polite">

            {#key $selectedTab$}
                <div class="subpage-container"
                     style:--gap={gap}
                     in:fly={{y: -10, duration: 250}}>

                    <slot activeTab={$selectedTab$.id}/>
                </div>
            {/key}
        </div>
    </div>
{/if}

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";
    @import (reference) "styles/portaro-erp.less";

    .erp-tabbed-subpages-container {
        .flex-grow();

        .tab-menu-container {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: @spacing-xl;
            padding: @spacing-ml @spacing-xxl;
            border-bottom: 1px solid @themed-border-default;
        }

        .subpage-container-wrapper {
            overflow-x: auto;
            position: relative;
            .flex-grow();

            &.with-padding .subpage-container {
                padding: @spacing-xl @spacing-xxl;
            }

            .subpage-container {
                gap: var(--gap);
                .flex-grow();

                :global {
                    .kp-record-grid-container {
                        .flex-grow();

                        .kp-record-grid-display {
                            .flex-grow();
                        }
                    }
                }
            }
        }
    }
</style>
